import React from 'react';
import { motion } from 'framer-motion';
import {
  FiPhoneIncoming, FiGitBranch, FiDatabase, FiBookOpen,
  FiCalendar, FiRefreshCw, FiBell, FiFileText, FiBarChart2,
  FiTrendingUp, FiUser,
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Features = () => {
  const inboundFeatures = [
    { icon: FiPhoneIncoming, title: '24/7 Call Handling', desc: 'Every call answered instantly — after hours, weekends, and holidays. No voicemail, no hold music.' },
    { icon: FiGitBranch, title: 'Technical Triage & Routing', desc: 'AI diagnoses the issue, routes to the right department, or resolves it directly without human involvement.' },
    { icon: FiDatabase, title: 'Ticket Creation & CRM Logging', desc: 'Automatically creates support tickets in Zendesk/ServiceNow/Freshdesk with full call context and transcript.' },
    { icon: FiBookOpen, title: 'FAQ & Knowledge Base Resolution', desc: 'Handles routine questions — order status, password resets, basic troubleshooting — without a human in the loop.' },
  ];

  const outboundFeatures = [
    { icon: FiCalendar, title: 'Appointment Confirmations', desc: 'Confirms service appointments 24 hours before to reduce no-shows and reschedule proactively.' },
    { icon: FiRefreshCw, title: 'Follow-Up Calls', desc: 'AI follows up on open tickets, checks resolution satisfaction, and closes the loop automatically.' },
    { icon: FiBell, title: 'Proactive Notifications', desc: 'Outbound calls for outage alerts, recall notices, service reminders, and scheduled maintenance windows.' },
  ];

  const techFeatures = [
    { icon: FiDatabase, title: 'Full CRM & Helpdesk Integration', desc: 'Salesforce, HubSpot, Zendesk, ServiceNow, Freshdesk, Jira Service Management — all auto-logged.' },
    { icon: FiFileText, title: 'Call Recording & Transcripts', desc: 'Every conversation recorded, transcribed, and searchable. Full audit trail for QA and compliance.' },
    { icon: FiBarChart2, title: 'Weekly Performance Reports', desc: 'Calls handled, resolution rate, average handle time, escalation rate — clear metrics every week.' },
    { icon: FiTrendingUp, title: 'Ongoing Optimization', desc: 'We analyze performance weekly and improve scripts, routing logic, and knowledge base coverage.' },
    { icon: FiUser, title: 'Direct Access to Founder', desc: 'Work directly with the founder during your pilot and onboarding period.' },
  ];

  const FeatureCard = ({ feature, bg = 'white' }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className={`p-5 rounded-2xl border border-slate-100 hover:border-slate-300 hover:shadow-lg transition-all duration-300 group ${bg === 'white' ? 'bg-white' : 'bg-white'}`}
    >
      <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center mb-3 group-hover:bg-slate-100 transition-colors">
        <SafeIcon icon={feature.icon} className="w-5 h-5 text-slate-700" />
      </div>
      <h3 className="font-bold text-slate-900 mb-2">{feature.title}</h3>
      <p className="text-sm text-slate-500 leading-relaxed">{feature.desc}</p>
    </motion.div>
  );

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">What's Included: AI Support System</h2>
          <p className="text-lg text-slate-600">Full inbound and outbound call handling — a complete support operations upgrade.</p>
        </div>

        <div className="mb-12">
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Inbound Call Handling</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {inboundFeatures.map((f, i) => <FeatureCard key={i} feature={f} />)}
          </div>
        </div>

        <div className="mb-12 bg-slate-50 p-6 rounded-2xl border border-slate-200">
          <div className="flex items-center gap-3 mb-6">
            <h3 className="text-2xl font-bold text-slate-900">Outbound Calling</h3>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-bold rounded-full">BETA — PILOT CLIENTS</span>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {outboundFeatures.map((f, i) => <FeatureCard key={i} feature={f} />)}
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-bold text-slate-900 mb-6">Technology & Support</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {techFeatures.map((f, i) => <FeatureCard key={i} feature={f} />)}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;